import { Link } from "react-router-dom";
import RevealWrapper from "../../base/RevealWrapper";
import { BUSINESS } from "../../../mocks/siteData";

export interface ServiceItem {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  detailServices: string[];
  icon: string;
  image: string;
  href: string;
}

export interface ServicesSectionContent {
  eyebrow: string;
  headline: string;
  subheadline: string;
  items: ServiceItem[];
}

interface Props {
  content: ServicesSectionContent;
}

const cardImages = [
  "https://public.readdy.ai/ai/img_res/e68509bbd7592339b4d39f7da5c1d6ec.jpg",
  "https://readdy.ai/api/search-image?query=close-up%20of%20glossy%20silky%20healthy%20hair%20with%20beautiful%20balayage%20highlights%2C%20warm%20golden%20champagne%20and%20brunette%20tones%2C%20hair%20gently%20moving%2C%20premium%20salon%20professional%20lighting%2C%20editorial%20luxury%20beauty%20photography%2C%20Allure%20magazine%20style%2C%20clean%20minimal%20white%20background&width=560&height=760&seq=svc-hair-v3-editorial&orientation=portrait",
  "https://readdy.ai/api/search-image?query=beautiful%20bronzed%20woman%20with%20glowing%20sun-kissed%20skin%20in%20warm%20golden%20light%2C%20professional%20luxury%20tanning%20result%2C%20close-up%20portrait%2C%20warm%20amber%20and%20gold%20lighting%2C%20clean%20cream%20background%2C%20premium%20beauty%20spa%20photography%2C%20radiant%20healthy%20glow%2C%20editorial%20style&width=560&height=760&seq=svc-tanning-v3-bronze&orientation=portrait",
];

export default function ServicesSection({ content }: Props) {
  const { eyebrow, headline, subheadline, items } = content;

  return (
    <section id="services" className="bg-[#FDFBF7] py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header — editorial two-column */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-4 md:mb-6">
          <RevealWrapper direction="left">
            <div className="max-w-xl">
              <div className="flex items-center gap-4 mb-5">
                <span className="section-eyebrow">{eyebrow}</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-charcoal-dark leading-tight">
                {headline}
              </h2>
            </div>
          </RevealWrapper>
          <RevealWrapper direction="right" delay={0.15}>
            <p className="font-sans text-sm text-charcoal/50 leading-relaxed max-w-xs lg:text-right font-light">
              {subheadline}
            </p>
          </RevealWrapper>
        </div>

        {/* Editorial pull-quote strip */}
        <RevealWrapper delay={0.1}>
          <div className="border-y border-cream-dark py-4 mb-12 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <i className="ri-award-line text-gold text-sm" />
                <span className="font-sans text-xs text-charcoal/55 tracking-wider">Voted Utah&apos;s Best Lashes</span>
              </div>
              <span className="w-px h-4 bg-cream-dark hidden sm:block" />
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-gold text-xs" />)}
                </div>
                <span className="font-sans text-xs text-charcoal/55">4.9 · 901 Reviews</span>
              </div>
            </div>
            <span className="font-serif text-sm text-charcoal/40 italic font-light">
              Lashes · Hair · Tanning — Lehi, Utah
            </span>
          </div>
        </RevealWrapper>

        {/* Service cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {items.map((service, i) => (
            <RevealWrapper key={service.id} delay={i * 0.12}>
              <article
                className={`service-card group relative overflow-hidden cursor-pointer ${i === 1 ? "md:mt-10" : ""}`}
                style={{ height: "540px" }}
              >
                {/* Card image */}
                <div className="service-card-inner absolute inset-0">
                  <img
                    src={cardImages[i]}
                    alt={`${service.title} — The Lash Bar Utah`}
                    title={`The Lash Bar ${service.title} — Lehi, Utah`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                {/* Gradient overlay */}
                <div className="service-card-overlay absolute inset-0" />

                {/* Service index — decorative */}
                <div className="absolute top-5 left-5 font-serif text-4xl font-light text-white/15 leading-none select-none">
                  {String(i + 1).padStart(2, "0")}
                </div>

                {/* Content at bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-7 z-10">
                  <span className="section-eyebrow text-gold/80 block mb-1.5">{service.subtitle}</span>
                  <h3 className="font-serif text-2xl md:text-3xl font-light text-white mb-2">
                    {service.title}
                  </h3>
                  <p className="font-sans text-sm text-white/55 leading-relaxed mb-4 font-light">
                    {service.description}
                  </p>

                  {/* Service list — reveals on hover */}
                  <div className="service-detail-list">
                    <ul className="space-y-1.5 mb-5">
                      {service.detailServices.slice(0, 4).map((s) => (
                        <li key={s} className="flex items-center gap-2 font-sans text-xs text-white/65">
                          <span className="w-1 h-1 bg-gold rounded-full flex-shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to={service.href}
                    className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.2em] uppercase text-gold hover:text-gold-light transition-colors cursor-pointer"
                  >
                    Explore Services
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>

                {/* Corner accent */}
                <div className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center">
                  <i className={`${service.icon} text-white/30 group-hover:text-gold text-sm transition-colors duration-300`} />
                </div>
              </article>
            </RevealWrapper>
          ))}
        </div>

        {/* Post-services CTA row */}
        <RevealWrapper delay={0.4}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-5 mt-14 pt-10 border-t border-cream-dark">
            <p className="font-serif text-xl text-charcoal/80 font-light italic">
              &ldquo;Utah&apos;s most trusted beauty studio — since day one.&rdquo;
            </p>
            <a
              href={BUSINESS.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-dark"
            >
              Experience Luxury Lash Extensions
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </RevealWrapper>
      </div>
    </section>
  );
}
